import React, { useState, useEffect, useMemo } from 'react';
import { getGPS, getPlayers, patchGPSMaxSpeed, getMssSquad } from '../../api';
import { useSeason, dateToSeason } from '../../SeasonContext';
import { useUrlParam } from '../../hooks/useUrlParam';
import { EXPOSURE_PCT, getSeasonStart, computeMaxSpeedSummary } from './maxSpeedShared';

// ── Helpers ────────────────────────────────────────────────────────────────────
// EXPOSURE_PCT/getSeasonStart/the individual-view summary math moved to maxSpeedShared.js
// (2026-09-10) so MaxSpeedPanel.js (the Einstein compose panel) reuses the exact same
// personal-max/season-max logic instead of a second hand-copy. Behavior here is unchanged.
function fmtDate(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

const fmtSpd  = n => (n == null || n <= 0) ? '—' : n.toFixed(1);
const fmtPct  = n => (n == null || n <= 0) ? '—' : n.toFixed(1) + '%';

const pctColor = pct => {
  if (pct == null || pct <= 0) return 'var(--text-muted)';
  if (pct >= 95) return '#10b981';
  if (pct >= 90) return '#f59e0b';
  return '#ef4444';
};

function speedFlag(pct) {
  if (pct == null || pct <= 0) return '—';
  if (pct >= 95) return '🟢';
  if (pct >= 90) return '🟡';
  return '🔴';
}

const ROW_LIMIT_OPTIONS = [5, 10, 20, 50, 'All'];

const ALERT_CFG = {
  pass: { color: '#10b981', rgba: '16,185,129',  icon: '✓' },
  fail: { color: '#ef4444', rgba: '239,68,68',   icon: '!' },
  none: { color: '#94a3b8', rgba: '148,163,184', icon: '—' },
};

// ── Shared small components ────────────────────────────────────────────────────
function AlertCard({ title, statusLine, detail, state }) {
  const cfg = ALERT_CFG[state] || ALERT_CFG.none;
  return (
    <div style={{ flex: 1, minWidth: 0, padding: '18px 20px', borderRadius: 14,
      border: `1px solid rgba(${cfg.rgba},0.3)`, background: `rgba(${cfg.rgba},0.07)` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
        <span style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
          background: `rgba(${cfg.rgba},0.2)`, border: `1px solid rgba(${cfg.rgba},0.5)`,
          color: cfg.color, fontWeight: 700, fontSize: '0.82rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{cfg.icon}</span>
      </div>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: cfg.color, marginBottom: 5 }}>{statusLine}</div>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{detail}</div>
    </div>
  );
}

function StatCard({ label, value, sub, valueColor }) {
  return (
    <div style={{ flex: 1, minWidth: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
      <div style={{ fontSize: '0.71rem', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: '1.7rem', fontWeight: 700, color: valueColor || 'var(--text-primary)', lineHeight: 1, marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  );
}

function SortHeader({ colKey, label, align, sortCol, sortDir, onSort }) {
  const active = sortCol === colKey;
  return (
    <th onClick={() => onSort(colKey)} style={{ ...TH(align), cursor: 'pointer', userSelect: 'none',
      color: active ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
      {label}
      {active && <span style={{ marginLeft: 4, fontSize: '0.65rem', opacity: 0.9 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}

function CheckMark({ pass }) {
  if (pass === null || pass === undefined)
    return <span style={{ color: 'var(--text-dimmed)' }}>—</span>;
  return <span style={{ fontWeight: 700, color: pass ? '#10b981' : '#ef4444', fontSize: '0.9rem' }}>{pass ? '✓' : '✗'}</span>;
}

function EmptyState({ text }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320, color: 'var(--text-muted)', textAlign: 'center', gap: 12 }}>
      <svg width="44" height="44" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2} opacity={0.4}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
      <div>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{text || 'No GPS data found for this player.'}</p>
        <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>Upload sessions in the Upload zone first.</p>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function MaxSpeed({ user, playerOverride = null }) {
  const { selectedSeasons } = useSeason();
  const [players, setPlayers]         = useState([]);
  const [sessions, setSessions]       = useState([]);
  const [playerId, setPlayerId]       = useUrlParam('player_id', '');
  const [view, setView]               = useState('individual');
  const [rowLimit, setRowLimit]       = useState(50);
  const [sortCol, setSortCol]         = useState('date');
  const [sortDir, setSortDir]         = useState('desc');
  const [teamSortCol, setTeamSortCol] = useState('lastPct');
  const [teamSortDir, setTeamSortDir] = useState('desc');
  const [editState, setEditState]     = useState(null); // { rowKey, sessionId, playerId, value, saving, error }
  const [loading, setLoading]         = useState(true);
  // MSS ceilings, keyed by player id (2026-09-08). This page used to define a player's max
  // speed as the single highest value ever recorded, which is the most artefact-exposed
  // statistic available and never comes down. It now shows mss_ceiling: a screened maximum
  // over a bounded window, carrying a confidence level and an observation count.
  const [mssByPlayer, setMssByPlayer] = useState({});

  useEffect(() => {
    // Separate from the main load so a missing/not-yet-migrated player_mss table degrades to
    // the old observed-maximum behaviour instead of blanking the page.
    getMssSquad()
      .then(rows => setMssByPlayer(Object.fromEntries((rows || []).map(r => [String(r.player_id), r]))))
      .catch(() => setMssByPlayer({}));
    Promise.all([getGPS(), getPlayers()])
      .then(([gps, pl]) => {
        setSessions(gps || []);
        const list = pl || [];
        setPlayers(list);
        const active = list.filter(p => p.active !== false);
        // Bug fix (2026-09-03): used to default to the first active player UNCONDITIONALLY,
        // discarding a `player_id` already in the URL — see PlayerChronic.js's identical fix
        // (window.location.search read directly, not the closure-captured useUrlParam binding —
        // that alone still showed the wrong player live; see that file's comment for why).
        const urlPlayerId = new URLSearchParams(window.location.search).get('player_id');
        if (playerOverride) setPlayerId(String(playerOverride.id)); else if (!urlPlayerId && active.length) setPlayerId(String(active[0].id));
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSort     = col => {
    if (sortCol === col)     setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col);     setSortDir('desc'); }
  };
  const handleTeamSort = col => {
    if (teamSortCol === col) setTeamSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTeamSortCol(col); setTeamSortDir('desc'); }
  };

  // ── Individual data ─────────────────────────────────────────────────────────
  // Individual-view summary (playerRecords/observedMax/personalMax/seasonMax/lastRecord) computed
  // via maxSpeedShared.js's computeMaxSpeedSummary (2026-09-10) — same function MaxSpeedPanel.js
  // (Einstein compose) uses, so a panel's numbers can never silently drift from this page's.
  const summary = useMemo(
    () => computeMaxSpeedSummary(playerId, sessions, mssByPlayer, selectedSeasons),
    [playerId, sessions, mssByPlayer, selectedSeasons]
  );
  const { playerRecords, observedMax, personalMax, seasonMax, lastRecord } = summary;

  const mssRow = mssByPlayer[String(playerId)] || null;
  const lastIsPersonalBest = lastRecord && observedMax != null && lastRecord.maxSpeed >= observedMax;

  const lastMD2 = useMemo(() => playerRecords.find(r => r.mdLabel === 'MD-2') || null, [playerRecords]);

  const last7Best = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7);
    const cutStr = cutoff.toISOString().slice(0, 10);
    const vals = playerRecords.filter(r => r.date >= cutStr).map(r => r.maxSpeed);
    return vals.length > 0 ? Math.max(...vals) : null;
  }, [playerRecords]);

  const tableRows = useMemo(() =>
    playerRecords.map(r => ({
      ...r,
      pct:  personalMax ? (r.maxSpeed / personalMax) * 100 : null,
      isPB: observedMax != null && r.maxSpeed >= observedMax,
    })),
    [playerRecords, personalMax, observedMax]);

  const sortedRows = useMemo(() => {
    const acc = { date: r => r.date, title: r => (r.title||'').toLowerCase(),
      mdLabel: r => r.mdLabel, maxSpeed: r => r.maxSpeed,
      pct: r => r.pct ?? -1, flag: r => r.pct ?? -1 }[sortCol] || (r => r.date);
    return [...tableRows].sort((a, b) => {
      const [av, bv] = [acc(a), acc(b)];
      return av < bv ? (sortDir==='asc'?-1:1) : av > bv ? (sortDir==='asc'?1:-1) : 0;
    });
  }, [tableRows, sortCol, sortDir]);

  const displayRows = useMemo(() =>
    rowLimit === 'All' ? sortedRows : sortedRows.slice(0, rowLimit),
    [sortedRows, rowLimit]);

  // ── Team data ───────────────────────────────────────────────────────────────
  const teamData = useMemo(() => {
    const cutoffSeason = getSeasonStart();
    const cutoff7 = new Date(); cutoff7.setDate(cutoff7.getDate() - 7);
    const cutStr7  = cutoff7.toISOString().slice(0, 10);

    return players.filter(p => p.active !== false).map(player => {
      const pid = String(player.id);
      const records = sessions
        .flatMap(s =>
          (s.players || [])
            .filter(p => String(p.player_id) === pid && Number(p.max_speed) > 0)
            .map(p => ({
              date:     (s.session_date || '').slice(0, 10),
              maxSpeed: Number(p.max_speed),
              mdLabel:  p.md_label || '',
              position: p.position || '',
            }))
        )
        .filter(r => {
            if (!r.date) return false;
            const season = dateToSeason(r.date);
            return !season || selectedSeasons.length === 0 || selectedSeasons.includes(season);
          })
        .sort((a, b) => b.date.localeCompare(a.date));

      if (!records.length) {
        return { id: player.id, name: player.name || `Player ${player.id}`,
          position: player.position || '—', personalMax: null, seasonMax: null,
          lastSpeed: null, lastPct: null, last7Pass: null, md2Pass: null,
          isPBInLastSession: false, hasData: false };
      }

      const observedMaxT = Math.max(...records.map(r => r.maxSpeed));
      const mssT     = mssByPlayer[String(player.id)] || null;
      const pMax     = mssT && mssT.mss_ceiling_kmh != null ? Number(mssT.mss_ceiling_kmh) : observedMaxT;
      const thresh   = pMax * (EXPOSURE_PCT / 100);
      const seasonVals = records.filter(r => r.date >= cutoffSeason).map(r => r.maxSpeed);
      const sMax     = seasonVals.length > 0 ? Math.max(...seasonVals) : null;
      const lastRec  = records[0];
      const lastPct  = (lastRec.maxSpeed / pMax) * 100;
      const last7Vals = records.filter(r => r.date >= cutStr7).map(r => r.maxSpeed);
      const last7B   = last7Vals.length > 0 ? Math.max(...last7Vals) : null;
      const last7Pass = last7B !== null ? last7B >= thresh : null;
      const lastMD2R  = records.find(r => r.mdLabel === 'MD-2');
      const md2Pass   = lastMD2R ? lastMD2R.maxSpeed >= thresh : null;
      const position  = player.position || records.find(r => r.position)?.position || '—';

      return { id: player.id, name: player.name || `Player ${player.id}`, position,
        personalMax: pMax, seasonMax: sMax, lastSpeed: lastRec.maxSpeed, lastPct,
        last7Pass, md2Pass, isPBInLastSession: lastRec.maxSpeed >= observedMaxT, hasData: true,
        confidence: mssT ? mssT.confidence : null, nObs: mssT ? mssT.n_eligible_obs : null };
    });
  }, [sessions, players, selectedSeasons, mssByPlayer]);

  const sortedTeamRows = useMemo(() => {
    const bv = v => v === true ? 1 : v === false ? 0 : -1;
    const acc = { name: r => (r.name||'').toLowerCase(), position: r => r.position||'',
      personalMax: r => r.personalMax??-1, seasonMax: r => r.seasonMax??-1,
      lastSpeed: r => r.lastSpeed??-1, lastPct: r => r.lastPct??-1,
      last7Pass: r => bv(r.last7Pass), md2Pass: r => bv(r.md2Pass) }[teamSortCol] || (r => r.lastPct??-1);
    return [...teamData].sort((a, b) => {
      const [av, bval] = [acc(a), acc(b)];
      return av < bval ? (teamSortDir==='asc'?-1:1) : av > bval ? (teamSortDir==='asc'?1:-1) : 0;
    });
  }, [teamData, teamSortCol, teamSortDir]);

  // ── Individual alert logic ──────────────────────────────────────────────────
  const threshold = personalMax ? personalMax * (EXPOSURE_PCT / 100) : null;
  const md2State  = !lastMD2 ? 'none' : threshold && lastMD2.maxSpeed >= threshold ? 'pass' : 'fail';
  const last7State = last7Best == null ? 'none' : threshold && last7Best >= threshold ? 'pass' : 'fail';

  const md2StatusLine  = lastMD2 ? `${fmtSpd(lastMD2.maxSpeed)} km/h  ·  ${fmtDate(lastMD2.date)}` : 'No MD-2 session found in data';
  const md2Detail      = threshold ? `${lastMD2 ? fmtPct(lastMD2.maxSpeed/personalMax*100)+' of personal max  ·  ' : ''}Threshold: ≥ ${fmtSpd(threshold)} km/h` : '';
  const last7StatusLine = last7Best != null ? `Best in last 7 days: ${fmtSpd(last7Best)} km/h` : 'No sessions recorded in last 7 days';
  const last7Detail    = threshold ? `${last7Best!=null ? fmtPct(last7Best/personalMax*100)+' of personal max  ·  ' : ''}Threshold: ≥ ${fmtSpd(threshold)} km/h` : '';

  // ── Team alert logic ────────────────────────────────────────────────────────
  const playersWithData = teamData.filter(p => p.hasData);
  const failed7   = playersWithData.filter(p => p.last7Pass  === false);
  const failedMD2 = playersWithData.filter(p => p.md2Pass    === false);
  const nameList  = arr => {
    const ns = arr.map(p => p.name.split(' ')[0]).slice(0, 3);
    return ns.join(', ') + (arr.length > 3 ? ` +${arr.length - 3} more` : '');
  };

  const team7State        = playersWithData.length === 0 ? 'none' : failed7.length   === 0 ? 'pass' : 'fail';
  const teamMD2State      = playersWithData.length === 0 ? 'none' : failedMD2.length === 0 ? 'pass' : 'fail';
  const team7StatusLine   = failed7.length   === 0 ? 'All players reached the exposure threshold in the last 7 days'   : `${failed7.length} player${failed7.length>1?'s':''} below threshold`;
  const teamMD2StatusLine = failedMD2.length === 0 ? 'All players reached the exposure threshold at MD-2'           : `${failedMD2.length} player${failedMD2.length>1?'s':''} below threshold`;
  const team7Detail       = failed7.length   > 0 ? nameList(failed7)   : `Threshold: ≥ ${EXPOSURE_PCT}% of MSS`;
  const teamMD2Detail     = failedMD2.length > 0 ? nameList(failedMD2) : `Threshold: ≥ ${EXPOSURE_PCT}% of MSS`;

  const handleSaveEdit = async () => {
    const newVal = parseFloat(editState.value);
    if (isNaN(newVal) || newVal <= 0) return;
    setEditState(s => ({ ...s, saving: true }));
    try {
      await patchGPSMaxSpeed(editState.sessionId, editState.playerId, newVal);
      setSessions(prev => prev.map(s => {
        if (s.id !== editState.sessionId) return s;
        return { ...s, players: s.players.map(p =>
          String(p.player_id) === editState.playerId ? { ...p, max_speed: newVal } : p
        )};
      }));
      setEditState(null);
    } catch (err) {
      console.error('Failed to update max speed:', err);
      setEditState(s => ({ ...s, saving: false, error: 'Save failed' }));
    }
  };

  if (loading) {
    return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', color:'var(--text-muted)', fontSize:'0.9rem' }}>Loading…</div>;
  }

  const hasIndividualData = playerRecords.length > 0;
  const lastPct = lastRecord && personalMax ? (lastRecord.maxSpeed / personalMax) * 100 : null;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', paddingBottom: 40 }}>

      {/* ── Header + view toggle ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Max Speed Analysis</h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>Personal top speed monitoring & sprint readiness</p>
        </div>
        {!playerOverride && (
          <div style={{ display: 'flex', gap: 0, borderRadius: 9, border: '1px solid var(--border-strong)', overflow: 'hidden', height: 36, flexShrink: 0 }}>
            {[['individual','Individual'],['team','Team']].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)} style={{
                padding: '0 20px', border: 'none', fontFamily: 'inherit', cursor: 'pointer', transition: 'all 0.15s',
                background: view === v ? '#2563eb' : 'transparent',
                color:      view === v ? '#fff' : 'var(--text-secondary)',
                fontSize: '0.84rem', fontWeight: view === v ? 600 : 400,
              }}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {/* ══════════════ INDIVIDUAL ══════════════ */}
      {view === 'individual' && (
        <>
          {!playerOverride && (
            <div style={{ marginBottom: 28 }}>
              <select value={playerId} onChange={e => setPlayerId(e.target.value)} style={selectStyle}>
                {[...players].filter(p => p.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(p => <option key={p.id} value={String(p.id)}>{p.name || p.username || `Player ${p.id}`}</option>)}
              </select>
            </div>
          )}

          {!hasIndividualData && <EmptyState />}

          {hasIndividualData && (
            <>
              <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
                <AlertCard title="MD-2 Sprint Check"       statusLine={md2StatusLine}  detail={md2Detail}  state={md2State} />
                <AlertCard title="Last 7 Days Sprint Check" statusLine={last7StatusLine} detail={last7Detail} state={last7State} />
              </div>

              <div style={{ display: 'flex', gap: 14, marginBottom: 28, flexWrap: 'wrap' }}>
                <StatCard label="MSS (ceiling)" value={`${fmtSpd(personalMax)} km/h`}
                  valueColor={mssRow && mssRow.confidence === "low" ? "#ef4444" : undefined}
                  sub={mssRow
                    ? `${(mssRow.confidence || "").toUpperCase()} confidence · n=${mssRow.n_eligible_obs} · ${mssRow.window_weeks}-week window`
                    : `Observed maximum (no MSS estimate yet) · highest recorded ${fmtSpd(observedMax)}`} />
                <StatCard label="Season Max Speed"   value={`${fmtSpd(seasonMax)} km/h`}   sub="Current season best" />
                <StatCard
                  label="Last Session"
                  value={<>{fmtSpd(lastRecord?.maxSpeed)} km/h{lastIsPersonalBest && <span style={{ marginLeft: 7 }}>🏆</span>}</>}
                  sub={lastRecord ? fmtDate(lastRecord.date) : '—'}
                  valueColor={pctColor(lastPct)}
                />
              </div>

              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 18px', borderBottom: '1px solid var(--border)', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>Session History</span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginLeft: 10 }}>{tableRows.length} sessions</span>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {ROW_LIMIT_OPTIONS.map(opt => (
                      <button key={opt} onClick={() => setRowLimit(opt)} style={limitBtn(rowLimit === opt)}>{opt}</button>
                    ))}
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-app)' }}>
                        {[
                          { key: 'date',     label: 'Date',      align: 'left'   },
                          { key: 'title',    label: 'Session',   align: 'left'   },
                          { key: 'maxSpeed', label: 'Max Speed', align: 'right'  },
                          { key: 'pct',      label: '% of Max',  align: 'right'  },
                          { key: 'flag',     label: 'Flag',      align: 'center' },
                        ].map(c => (
                          <SortHeader key={c.key} colKey={c.key} label={c.label} align={c.align}
                            sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map((r, i) => {
                        const rowBg = r.pct >= 95
                          ? 'rgba(16,185,129,0.07)'
                          : i % 2 !== 0 ? 'var(--bg-row-alt)' : 'transparent';
                        return (
                          <tr key={i} style={{ background: rowBg }}>
                            <td style={TD('left')}>{fmtDate(r.date)}</td>
                            <td style={{ ...TD('left'), maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{r.title || '—'}</td>
                            <td style={{ ...TD('right'), fontWeight: 600, color: pctColor(r.pct) }}>
                              {user?.isAdmin && editState?.rowKey === `${r.sessionId}-${r.playerId}` ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                                  <input
                                    type="number" step="0.1" min="0"
                                    value={editState.value}
                                    onChange={e => setEditState(s => ({ ...s, value: e.target.value }))}
                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditState(null); }}
                                    autoFocus
                                    style={{ width: 68, padding: '3px 6px', borderRadius: 6, border: '1px solid #3b82f6', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: '0.82rem', outline: 'none', fontFamily: 'inherit' }}
                                  />
                                  <button onClick={handleSaveEdit} disabled={editState.saving}
                                    style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)', color: '#10b981', borderRadius: 5, padding: '2px 7px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>
                                    ✓
                                  </button>
                                  <button onClick={() => setEditState(null)}
                                    style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: 5, padding: '2px 7px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>
                                    ✗
                                  </button>
                                  {editState.error && <span style={{ fontSize: '0.72rem', color: '#ef4444', marginLeft: 2 }}>{editState.error}</span>}
                                </div>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                                  <span>{fmtSpd(r.maxSpeed)}{r.isPB && <span style={{ marginLeft: 5, fontSize: '0.82rem' }}>🏆</span>}</span>
                                  {user?.isAdmin && (
                                    <button
                                      onClick={() => setEditState({ rowKey: `${r.sessionId}-${r.playerId}`, sessionId: r.sessionId, playerId: r.playerId, value: String(r.maxSpeed), saving: false })}
                                      title="Correct max speed"
                                      style={{ background: 'none', border: 'none', padding: '1px 3px', cursor: 'pointer', color: 'var(--text-dimmed)', borderRadius: 4, lineHeight: 1, opacity: 0.6 }}
                                      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                      onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
                                    >
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td style={{ ...TD('right'), fontWeight: 600, color: pctColor(r.pct) }}>{fmtPct(r.pct)}</td>
                            <td style={{ ...TD('center'), fontSize: '1rem' }}>{speedFlag(r.pct)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ══════════════ TEAM ══════════════ */}
      {view === 'team' && (
        <>
          {playersWithData.length === 0 && <EmptyState text="No GPS data found. Upload sessions in the Upload zone first." />}

          {playersWithData.length > 0 && (
            <>
              <div style={{ display: 'flex', gap: 14, marginBottom: 28, flexWrap: 'wrap' }}>
                <AlertCard title="Last 7 Days — Below Threshold"  statusLine={team7StatusLine}   detail={team7Detail}   state={team7State} />
                <AlertCard title="MD-2 — Below Threshold"         statusLine={teamMD2StatusLine} detail={teamMD2Detail} state={teamMD2State} />
              </div>

              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>Squad Overview</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginLeft: 10 }}>{playersWithData.length} players with data</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-app)' }}>
                        {[
                          { key: 'name',        label: 'Player',           align: 'left'   },
                          { key: 'position',    label: 'Pos.',             align: 'center' },
                          { key: 'personalMax', label: 'Personal Max',     align: 'right'  },
                          { key: 'seasonMax',   label: 'Season Max',       align: 'right'  },
                          { key: 'lastSpeed',   label: 'Last Session',     align: 'right'  },
                          { key: 'lastPct',     label: '% of Max',         align: 'right'  },
                          { key: 'last7Pass',   label: `Last 7d ≥ ${EXPOSURE_PCT}%`,   align: 'center' },
                          { key: 'md2Pass',     label: `MD-2 ≥ ${EXPOSURE_PCT}%`,      align: 'center' },
                        ].map(c => (
                          <SortHeader key={c.key} colKey={c.key} label={c.label} align={c.align}
                            sortCol={teamSortCol} sortDir={teamSortDir} onSort={handleTeamSort} />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTeamRows.map((r, i) => {
                        const bothFail = r.last7Pass === false && r.md2Pass === false;
                        const rowBg = bothFail
                          ? 'rgba(239,68,68,0.07)'
                          : i % 2 !== 0 ? 'var(--bg-row-alt)' : 'transparent';
                        return (
                          <tr key={r.id} style={{ background: rowBg }}>
                            <td style={TD('left')}>
                              <button onClick={() => { setPlayerId(String(r.id)); setView('individual'); }}
                                style={{ background: 'none', border: 'none', padding: 0, fontFamily: 'inherit',
                                  fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                                  color: 'var(--text-primary)', textDecoration: 'underline',
                                  textDecorationColor: 'var(--border-strong)' }}>
                                {r.name}
                              </button>
                              {r.isPBInLastSession && <span style={{ marginLeft: 6, fontSize: '0.8rem' }}>🏆</span>}
                            </td>
                            <td style={{ ...TD('center'), color: 'var(--text-secondary)', fontSize: '0.76rem' }}>{r.position}</td>
                            <td style={{ ...TD('right'), fontWeight: 600 }}>{fmtSpd(r.personalMax)}</td>
                            <td style={{ ...TD('right'), color: 'var(--text-secondary)' }}>{fmtSpd(r.seasonMax)}</td>
                            <td style={{ ...TD('right'), fontWeight: 600, color: pctColor(r.lastPct) }}>{fmtSpd(r.lastSpeed)}</td>
                            <td style={{ ...TD('right'), fontWeight: 600, color: pctColor(r.lastPct) }}>{fmtPct(r.lastPct)}</td>
                            <td style={TD('center')}><CheckMark pass={r.last7Pass} /></td>
                            <td style={TD('center')}><CheckMark pass={r.md2Pass} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────
const selectStyle = {
  padding: '8px 32px 8px 12px', borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.88rem',
  fontFamily: 'inherit', cursor: 'pointer', outline: 'none', minWidth: 190,
  appearance: 'none', WebkitAppearance: 'none',
};

const limitBtn = active => ({
  padding: '5px 11px', borderRadius: 7, fontFamily: 'inherit', cursor: 'pointer',
  border:      active ? '1px solid #3b82f6'          : '1px solid var(--border)',
  background:  active ? 'rgba(59,130,246,0.15)'       : 'transparent',
  color:       active ? '#93c5fd'                      : 'var(--text-muted)',
  fontSize: '0.79rem', fontWeight: active ? 600 : 400,
});

const TH = align => ({
  padding: '9px 12px', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: 'var(--text-muted)', textAlign: align,
  whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)', background: 'var(--bg-app)',
});

const TD = align => ({
  padding: '9px 12px', fontSize: '0.82rem', color: 'var(--text-primary)',
  textAlign: align, borderBottom: '1px solid var(--border)',
});
